import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import PagamentosSection, {
  PAPEL_ANALISTA,
} from "./PagamentosSection";
import PreviaModeloPublicacaoRecebimento from "./PreviaModeloPublicacaoRecebimento";
import publicacaoService from "../services/publicacao";
import {
  statusLabels,
  statusPublicacaoEnum,
  statusPublicacaoLabels,
  tipoPublicacaoLabels,
} from "../utils/constants";
import { getMuiChipColor, getPublicacaoPaletteKey } from "../utils/statusPalette";
import { formatDateToShow } from "../utils/formatters";

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function PublicacaoAnalistaModal({ open, onClose, publicacao, onAtualizado }) {
  const [item, setItem] = useState(publicacao);
  const [novoStatus, setNovoStatus] = useState("");
  const [modelos, setModelos] = useState([]);
  const [modeloSelecionado, setModeloSelecionado] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setItem(publicacao);
    setNovoStatus("");
    setModeloSelecionado("");
    setError("");
  }, [publicacao]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const carregarModelos = async () => {
      setLoadingModelos(true);
      try {
        const lista = await publicacaoService.listarModelosRecebimento();
        if (!cancelled) {
          setModelos(Array.isArray(lista) ? lista : []);
        }
      } catch {
        if (!cancelled) {
          setModelos([]);
          setError("Erro ao carregar modelos de publicação de recebimento.");
        }
      } finally {
        if (!cancelled) setLoadingModelos(false);
      }
    };

    carregarModelos();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!item) return null;

  const podeEfetuar =
    item.status === statusPublicacaoEnum.SOLICITADA ||
    item.status === statusPublicacaoEnum.PAGAMENTO_EFETUADO;

  const handleAtualizarStatus = async () => {
    if (novoStatus === "" || novoStatus === null || novoStatus === undefined) return;
    setLoading(true);
    setError("");
    try {
      const updated = await publicacaoService.atualizarStatus(item.id, Number(novoStatus));
      setItem(updated);
      onAtualizado?.(updated);
    } catch (err) {
      setError(err?.response?.data?.message || "Erro ao atualizar status.");
    } finally {
      setLoading(false);
    }
  };

  const handleEfetuar = async () => {
    if (modeloSelecionado === "" || modeloSelecionado === null || modeloSelecionado === undefined) {
      setError("Selecione o modelo de publicação antes de efetuar.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const updated = await publicacaoService.efetuar(item.id, Number(modeloSelecionado));
      setItem(updated);
      onAtualizado?.(updated);
    } catch (err) {
      setError(err?.response?.data?.message || "Erro ao efetuar publicação de recebimento.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadComprovanteAgricultor = async () => {
    try {
      const blob = await publicacaoService.baixarComprovanteAgricultorFamiliar(item.id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      setError("Erro ao baixar comprovante de agricultor familiar.");
    }
  };

  const handleDownloadComprovantePdf = async () => {
    try {
      const blob = await publicacaoService.baixarComprovante(item.id);
      downloadBlob(blob, `comprovante-publicacao-${item.id}.pdf`);
    } catch {
      setError("Erro ao baixar comprovante da publicação.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Publicação de recebimento — análise</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Typography>
            <strong>Tipo:</strong>{" "}
            {tipoPublicacaoLabels[item.tipo] ?? item.tipo ?? "—"}
          </Typography>
          <Typography>
            <strong>Empreendimento:</strong> {item.empreendimentoNome || "—"}
          </Typography>
          <Typography>
            <strong>CPF/CNPJ:</strong> {item.empreendimentoCnpjCpf || item.cnpj || "—"}
          </Typography>
          <Typography>
            <strong>Tipo de processo:</strong> {item.tipoLicencaNome || "—"}
          </Typography>
          <Typography>
            <strong>Abertura da solicitação:</strong>{" "}
            {item.dataAberturaSolicitacao
              ? formatDateToShow(item.dataAberturaSolicitacao)
              : "—"}
          </Typography>
          <Typography>
            <strong>Validade da licença:</strong>{" "}
            {item.dataValidade ? formatDateToShow(item.dataValidade) : "—"}
          </Typography>
          <Typography>
            <strong>Situação da licença:</strong>{" "}
            {statusLabels[item.statusSolicitacao] ?? item.statusSolicitacao ?? "—"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography>
              <strong>Status da publicação:</strong>
            </Typography>
            <Chip
              size="small"
              label={statusPublicacaoLabels[item.status] ?? item.status}
              color={getMuiChipColor(getPublicacaoPaletteKey(item.status))}
            />
          </Box>
          <Typography>
            <strong>Agricultor familiar / baixa renda:</strong>{" "}
            {item.isAgricultorFamiliarOuBaixaRenda ? "Sim" : "Não"}
          </Typography>
          {item.temComprovanteAgricultorFamiliarOuBaixaRenda && (
            <Button variant="outlined" size="small" onClick={handleDownloadComprovanteAgricultor}>
              Baixar comprovante de agricultor familiar
            </Button>
          )}
          {item.temComprovantePdf && (
            <Button variant="outlined" size="small" onClick={handleDownloadComprovantePdf}>
              Baixar comprovante da publicação
            </Button>
          )}
        </Stack>

        {item.status !== statusPublicacaoEnum.PUBLICADA && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3 }}>
            <TextField
              select
              size="small"
              label="Atualizar status"
              value={novoStatus}
              onChange={(e) => setNovoStatus(e.target.value)}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value={statusPublicacaoEnum.AGUARDANDO_PAGAMENTO}>
                Aguardando Pagamento
              </MenuItem>
              <MenuItem value={statusPublicacaoEnum.PAGAMENTO_EFETUADO}>
                Pagamento Efetuado
              </MenuItem>
              <MenuItem value={statusPublicacaoEnum.RECUSADA}>Recusada</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              disabled={loading || novoStatus === ""}
              onClick={handleAtualizarStatus}
            >
              Salvar status
            </Button>
          </Box>
        )}

        {podeEfetuar && (
          <Stack spacing={2} sx={{ mb: 3 }}>
            <TextField
              select
              size="small"
              required
              label="Modelo de publicação"
              value={modeloSelecionado}
              onChange={(e) => setModeloSelecionado(e.target.value)}
              disabled={loadingModelos || loading}
              sx={{ minWidth: 320, maxWidth: 520 }}
              helperText={
                loadingModelos
                  ? "Carregando modelos..."
                  : "Escolha o modelo oficial que será publicado no site."
              }
            >
              {modelos.map((modelo) => (
                <MenuItem key={modelo.codigo} value={modelo.codigo}>
                  {modelo.nome}
                </MenuItem>
              ))}
            </TextField>

            {modeloSelecionado !== "" && (
              <PreviaModeloPublicacaoRecebimento
                modelo={Number(modeloSelecionado)}
                nomeOuRazaoSocial={item.empreendimentoNome || item.nomeOuRazaoSocial}
                tipoLicencaNome={item.tipoLicencaNome}
                atividade={item.atividadeNome}
                endereco={item.endereco}
                dataValidade={item.dataValidade}
              />
            )}
          </Stack>
        )}

        <PagamentosSection
          solicitacao={{ id: item.id }}
          papel={PAPEL_ANALISTA}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Fechar
        </Button>
        {podeEfetuar && (
          <Button
            variant="contained"
            color="success"
            onClick={handleEfetuar}
            disabled={loading || loadingModelos || modeloSelecionado === ""}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Efetuar publicação"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
